import React, { useMemo } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import {
    Paper,
    Typography,
    Grid,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Card,
    CardContent,
} from '@mui/material';
import { liveTrackinglayoutSelector } from '../../../features/layout/layoutSlice';
import { formatDurationDHMS } from '../../../utils/timeFormat';
import { renderAddressMapLink } from './reportMapLinks';

const GeneratorFleet = ({ reportData }) => {
    const dimensions = useSelector(liveTrackinglayoutSelector);
    const vehicles = Array.isArray(reportData?.vehicles) ? reportData.vehicles : [];
    const timeFrom = reportData?.timeFrom || reportData?.reqBody?.timeFrom || null;
    const timeTo = reportData?.timeTo || reportData?.reqBody?.timeTo || null;

    const summaryRows = useMemo(() => {
        return vehicles.map((vehicle) => ({
            vehicleName: vehicle?.vehicleName || '--',
            imei: vehicle?.imei || '--',
            eventSource: vehicle?.eventSource || '--',
            routeLength: Number(vehicle?.routeLength || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 }),
            onDuration: formatDurationDHMS(vehicle?.generatorOnDurationMin || 0),
            offDuration: formatDurationDHMS(vehicle?.generatorOffDurationMin || 0),
        }));
    }, [vehicles]);

    if (!reportData) return null;

    return (
        <Paper style={{ ...dimensions.reportDetailViewBottom, overflow: 'auto' }}>
            <div style={{ margin: '10px' }}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <Card variant="outlined" style={{ backgroundColor: 'White' }}>
                            <CardContent>
                                <Typography variant="h6" style={{ color: 'RoyalBlue' }}>
                                    <b>Report Summary</b>
                                </Typography>
                                <Divider style={{ marginBottom: '10px' }} />
                                <Grid container spacing={1}>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="body2" color="textSecondary">From</Typography>
                                        <Typography variant="body1" color="textPrimary">
                                            {timeFrom ? moment(timeFrom).format('Do MMM YY hh:mm A') : '--'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="body2" color="textSecondary">To</Typography>
                                        <Typography variant="body1" color="textPrimary">
                                            {timeTo ? moment(timeTo).format('Do MMM YY hh:mm A') : '--'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="textSecondary">Vehicles</Typography>
                                        <Typography variant="body1" color="textPrimary">
                                            {vehicles.length}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><b>Vehicle</b></TableCell>
                                        <TableCell><b>IMEI</b></TableCell>
                                        <TableCell><b>Event Source</b></TableCell>
                                        <TableCell align="right"><b>Route (km)</b></TableCell>
                                        <TableCell><b>Gen ON</b></TableCell>
                                        <TableCell><b>Gen OFF</b></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {summaryRows.map((row, index) => (
                                        <TableRow key={`${row.imei}-${index}`}>
                                            <TableCell>{row.vehicleName}</TableCell>
                                            <TableCell>{row.imei}</TableCell>
                                            <TableCell>{row.eventSource}</TableCell>
                                            <TableCell align="right">{row.routeLength}</TableCell>
                                            <TableCell>{row.onDuration}</TableCell>
                                            <TableCell>{row.offDuration}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>

                    {vehicles.map((vehicle, index) => (
                        <Grid item xs={12} key={`${vehicle?.imei || 'vehicle'}-${index}`}>
                            <Card variant="outlined" style={{ backgroundColor: 'White' }}>
                                <CardContent>
                                    <Typography variant="subtitle1" style={{ color: 'Navy', marginBottom: '6px' }}>
                                        <b>{vehicle?.vehicleName || 'Vehicle'}</b>
                                    </Typography>
                                    {(vehicle?.runningPeriods || []).length === 0 && (
                                        <Typography variant="body2" color="textSecondary">
                                            No running periods found.
                                        </Typography>
                                    )}
                                    {(vehicle?.runningPeriods || []).length > 0 && (
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell><b>Start</b></TableCell>
                                                        <TableCell><b>End</b></TableCell>
                                                        <TableCell><b>Duration</b></TableCell>
                                                        <TableCell align="right"><b>Distance (km)</b></TableCell>
                                                        <TableCell><b>Side</b></TableCell>
                                                        <TableCell><b>Start Address</b></TableCell>
                                                        <TableCell><b>End Address</b></TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {vehicle.runningPeriods.map((period, pIndex) => (
                                                        <TableRow key={`${vehicle?.imei}-${pIndex}`}>
                                                            <TableCell>{period?.startTime ? moment(period.startTime).format('Do MMM YY hh:mm A') : '--'}</TableCell>
                                                            <TableCell>{period?.endTime ? moment(period.endTime).format('Do MMM YY hh:mm A') : '--'}</TableCell>
                                                            <TableCell>{formatDurationDHMS(period?.durationMinutes || 0)}</TableCell>
                                                            <TableCell align="right">{Number(period?.distance || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</TableCell>
                                                            <TableCell>{String(period?.generatorSide || '').toUpperCase() || '--'}</TableCell>
                                                            <TableCell>
                                                                {renderAddressMapLink({
                                                                    address: period?.startAddress,
                                                                    source: period,
                                                                    type: 'start',
                                                                })}
                                                            </TableCell>
                                                            <TableCell>
                                                                {renderAddressMapLink({
                                                                    address: period?.endAddress,
                                                                    source: period,
                                                                    type: 'end',
                                                                })}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </div>
        </Paper>
    );
};

export default GeneratorFleet;
